import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageLoadingState } from '@/components/layout/PageLoadingState';
import { Button } from '@/components/ui/button';
import { DataSurface } from '@/components/platform/DataSurface';
import { MetricStrip } from '@/components/platform/MetricStrip';
import AccountsFilters, { AccountsFilterState } from '@/components/accounts/AccountsFilters';
import AccountsTable from '@/components/accounts/AccountsTable';
import { useAuth } from '@/hooks/useAuthApi';
import { useOrganization } from '@/hooks/useOrganization';
import { useAccountsList } from '@/hooks/accounts/useAccountsList';
import { useAccountsMetrics } from '@/hooks/accounts/useAccountsMetrics';
import accountsService, {
  AccountSortBy,
  SortDirection,
} from '@/lib/api/accounts';
import { useToast } from '@/components/ui/use-toast';

const PAGE_SIZE = 50;

const initialFilters: AccountsFilterState = {
  search: '',
  health: [],
  minUsers: undefined,
  datePreset: 'last_30_days',
};

const DATE_PRESET_LABELS: Record<string, string> = {
  last_7_days: 'Last 7 days',
  last_30_days: 'Last 30 days',
  last_90_days: 'Last 90 days',
  all_time: 'All time',
};

const formatMetricTrend = (
  change: number | null | undefined,
): { value: string; tone: 'positive' | 'negative' | 'neutral' } | undefined => {
  if (change == null) return undefined;
  if (change === 0) return { value: '0%', tone: 'neutral' };
  const abs = Math.abs(change);
  const compactValue = abs >= 1000 ? `${(abs / 100).toFixed(1)}x` : `${abs}%`;
  return {
    value: `${change > 0 ? '+' : '-'}${compactValue}`,
    tone: change > 0 ? 'positive' : 'negative',
  };
};

const AccountsPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { currentOrganization, isLoading: orgLoading } = useOrganization();
  const { toast } = useToast();

  const [filters, setFilters] = useState<AccountsFilterState>(() => ({ ...initialFilters }));
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<AccountSortBy>('last_seen_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isExporting, setIsExporting] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Reset pagination when filters change
  useEffect(() => {
    setPage(1);
  }, [filters.health, filters.minUsers, filters.datePreset, debouncedSearch]);

  const dateFilters = useMemo(() => {
    if (filters.datePreset === 'all_time') {
      return { date_from: undefined, date_to: undefined };
    }
    const now = new Date();
    const days =
      filters.datePreset === 'last_7_days'
        ? 7
        : filters.datePreset === 'last_90_days'
          ? 90
          : 30;
    const from = new Date(now);
    from.setDate(from.getDate() - days);
    return {
      date_from: from.toISOString(),
      date_to: now.toISOString(),
    };
  }, [filters.datePreset]);

  const healthCsv = filters.health.length > 0 ? filters.health.join(',') : undefined;

  const { accounts, total, isLoading, error } = useAccountsList(currentOrganization?.id, {
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    search: debouncedSearch || undefined,
    health: healthCsv,
    min_users: filters.minUsers,
    date_from: dateFilters.date_from,
    date_to: dateFilters.date_to,
    sort_by: sortBy,
    sort_direction: sortDirection,
    enabled: !!currentOrganization?.id,
  });

  const { data: metrics, isLoading: metricsLoading } = useAccountsMetrics(
    currentOrganization?.id,
    { date_from: dateFilters.date_from, date_to: dateFilters.date_to },
  );

  // Keep page in bounds when result count shrinks
  useEffect(() => {
    if (!total) return;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (page > totalPages) setPage(1);
  }, [page, total]);

  const clearFilters = useCallback(() => {
    setFilters({ ...initialFilters });
  }, []);

  const handleExportCsv = useCallback(async () => {
    if (!currentOrganization?.id) return;
    setIsExporting(true);
    try {
      await accountsService.exportAccountsCsv(currentOrganization.id, {
        search: debouncedSearch || undefined,
        health: healthCsv,
        min_users: filters.minUsers,
        date_from: dateFilters.date_from,
        date_to: dateFilters.date_to,
        sort_by: sortBy,
        sort_direction: sortDirection,
      });
    } catch (e) {
      console.error('Accounts CSV export failed:', e);
      toast({
        title: 'CSV export failed',
        description: 'Unable to download the export right now. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  }, [
    currentOrganization?.id,
    debouncedSearch,
    healthCsv,
    filters.minUsers,
    dateFilters,
    sortBy,
    sortDirection,
    toast,
  ]);

  const handleSortChange = useCallback(
    (next_sort_by: AccountSortBy, next_direction: SortDirection) => {
      setSortBy(next_sort_by);
      setSortDirection(next_direction);
      setPage(1);
    },
    [],
  );

  if (authLoading || orgLoading) {
    return <PageLoadingState />;
  }

  if (!user) {
    return <div className="p-4 text-center">Please log in to view accounts.</div>;
  }

  if (!currentOrganization?.id) {
    return <div className="p-4 text-center">Select an organization to view accounts.</div>;
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <Helmet>
        <title>Accounts | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
        <PageHeader
          title="Accounts"
          titleTooltip="Company-level engagement across people and sessions."
        />

        <div className="px-4 pb-3 pt-4 sm:px-6 md:px-8">
          <div className="mb-3">
            <h2 className="text-[13px] font-medium text-foreground">Account intelligence</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Company rollups for {DATE_PRESET_LABELS[filters.datePreset] ?? filters.datePreset}, sorted by recent activity.
            </p>
          </div>
          <MetricStrip
            items={[
              {
                label: 'Accounts',
                value: metricsLoading ? '...' : (metrics?.total_accounts ?? 0).toLocaleString(),
                meta: 'Company domains',
                trend: formatMetricTrend(metrics?.total_accounts_change),
              },
              {
                label: 'Avg users',
                value: metricsLoading ? '...' : (metrics?.avg_users_per_account ?? 0),
                meta: 'Per account',
                trend: formatMetricTrend(metrics?.avg_users_per_account_change),
              },
              {
                label: 'High intent',
                value: metricsLoading ? '...' : (metrics?.total_high_intent_users ?? 0).toLocaleString(),
                meta: 'Users',
                trend: formatMetricTrend(metrics?.total_high_intent_users_change),
              },
              {
                label: 'Qualified',
                value: metricsLoading ? '...' : (metrics?.total_qualified_users ?? 0).toLocaleString(),
                meta: 'Users',
                trend: formatMetricTrend(metrics?.total_qualified_users_change),
              },
            ]}
          />
        </div>

        <div className="px-4 pb-3 sm:px-6 md:px-8">
          <AccountsFilters
            filters={filters}
            onFiltersChange={(patch) => setFilters((prev) => ({ ...prev, ...patch }))}
            onReset={clearFilters}
            onExportCsv={handleExportCsv}
            isExporting={isExporting}
          />
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto px-4 sm:px-6 md:px-8">
          {error && (
            <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error instanceof Error ? error.message : 'Failed to load accounts'}
            </div>
          )}
          <DataSurface>
            <AccountsTable
              accounts={accounts}
              isLoading={isLoading}
              offset={(page - 1) * PAGE_SIZE}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
            />
          </DataSurface>

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between py-4">
              <span className="text-sm text-muted-foreground">
                Showing {(page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AccountsPage;
